import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Browser Push Notification Manager
class BrowserNotificationManager {
  constructor() {
    this.permission = 'default';
    this.checkPermission();
  }

  checkPermission() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Browser does not support notifications');
    }

    const permission = await Notification.requestPermission();
    this.permission = permission;
    return permission === 'granted';
  }

  async show(title, options = {}) {
    if (this.permission !== 'granted') {
      const granted = await this.requestPermission();
      if (!granted) {
        console.warn('Notification permission denied');
        return null;
      }
    }

    const notification = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      ...options
    });

    return notification;
  }

  isSupported() {
    return 'Notification' in window;
  }

  isGranted() {
    return this.permission === 'granted';
  }
}

export const browserNotifications = new BrowserNotificationManager();

// Notification Service
export const notificationService = {
  // Get all notifications for current user
  async getAll(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (filters?.isRead !== undefined) {
      query = query?.eq('is_read', filters?.isRead);
    }

    if (filters?.notificationType) {
      query = query?.eq('notification_type', filters?.notificationType);
    }

    if (filters?.orderId) {
      query = query?.eq('order_id', filters?.orderId);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return toCamelCase(data);
  },

  // Get unread notifications
  async getUnread() {
    return this.getAll({ isRead: false });
  },

  // Get unread count
  async getUnreadCount() {
    const { data, error } = await supabase?.rpc('get_unread_notification_count');
    if (error) throw error;
    return data || 0;
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    const { error } = await supabase?.rpc('mark_notification_read', {
      p_notification_id: notificationId
    });
    if (error) throw error;
  },

  // Mark all notifications as read
  async markAllAsRead() {
    const { error } = await supabase?.rpc('mark_all_notifications_read');
    if (error) throw error;
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(callback) {
    const { data: { user } } = supabase?.auth?.getUser();
    if (!user) {
      console.error('Cannot subscribe: user not authenticated');
      return null;
    }

    const channel = supabase
      ?.channel('notifications')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          const notification = toCamelCase(payload?.new);
          
          // Show browser notification if enabled
          if (notification?.channel === 'push' || notification?.channel === 'both') {
            this.showBrowserNotification(notification);
          }
          
          // Call callback with new notification
          if (callback) {
            callback(notification);
          }
        }
      )
      ?.subscribe();

    return channel;
  },

  // Unsubscribe from notifications
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // Show browser notification
  async showBrowserNotification(notification) {
    try {
      if (!browserNotifications?.isSupported()) {
        console.warn('Browser notifications not supported');
        return;
      }

      await browserNotifications?.show(notification?.title, {
        body: notification?.message,
        tag: notification?.id,
        data: notification,
        requireInteraction: false,
        silent: false
      });
    } catch (error) {
      console.error('Failed to show browser notification:', error);
    }
  },

  // Request browser notification permission
  async requestBrowserPermission() {
    return browserNotifications?.requestPermission();
  },

  // Check if browser notifications are enabled
  isBrowserNotificationEnabled() {
    return browserNotifications?.isGranted();
  },

  // Get notification preferences
  async getPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.single();

    if (error) {
      // If no preferences exist, return defaults
      if (error?.code === 'PGRST116') {
        return this.createDefaultPreferences();
      }
      throw error;
    }

    return toCamelCase(data);
  },

  // Create default preferences
  async createDefaultPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.insert({ user_id: user?.id })
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Update notification preferences
  async updatePreferences(preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeCasePrefs = toSnakeCase(preferences);
    delete snakeCasePrefs?.id;
    delete snakeCasePrefs?.user_id;
    delete snakeCasePrefs?.created_at;
    delete snakeCasePrefs?.updated_at;

    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.update({
        ...snakeCasePrefs,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('user_id', user?.id)
      ?.select();

    if (error) throw error;
    return toCamelCase(data?.[0]);
  },

  // Get notifications for specific order
  async getOrderNotifications(orderId) {
    return this.getAll({ orderId });
  },

  // Delete notification
  async delete(notificationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId)
      ?.eq('user_id', user?.id);

    if (error) throw error;
  },

  // Get notification statistics
  async getStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('notifications')
      ?.select('notification_type, is_read, channel')
      ?.eq('user_id', user?.id);

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      unread: data?.filter(n => !n?.is_read)?.length || 0,
      read: data?.filter(n => n?.is_read)?.length || 0,
      byType: {},
      byChannel: {
        push: data?.filter(n => n?.channel === 'push' || n?.channel === 'both')?.length || 0,
        email: data?.filter(n => n?.channel === 'email' || n?.channel === 'both')?.length || 0,
        both: data?.filter(n => n?.channel === 'both')?.length || 0
      }
    };

    // Count by type
    data?.forEach(notification => {
      const type = notification?.notification_type;
      stats.byType[type] = (stats?.byType?.[type] || 0) + 1;
    });

    return stats;
  }
};

export default notificationService;